/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {getCurrentUser, loadUsers, showUserProfileDialog} from '@/store/actions/dashboard';
import {signout} from '@/store/actions/login';
import {isAuthenticatingSelector, userSelector} from '@/store/selectors/login-selectors';

export interface UserProfileMobileProps {
  showProfile: boolean;
  handleClose: () => void;
}

const useStyles = makeStyles(theme => ({
  mainContainer : {
    margin       : '40px',
    marginBottom : 0
  },
  subContainer : {
    display      : 'flex',
    width        : '100%',
    borderBottom : `1px solid ${dlsColor('grey4')}`
  },
  user : {
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    fontWeight : 500
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  },
  profileItem : {
    width          : '100%',
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  ctaLink : {
    display    : 'flex',
    alignItems : 'center'
  },
  container : {
    display      : 'flex',
    width        : '100%',
    padding      : 8,
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  profileTitle : {
    width     : '100%',
    textAlign : 'center',
    marginTop : 6
  },
  userProfileHeader : {
    display       : 'flex',
    textTransform : 'uppercase',
    fontSize      : '20px',
    weight        : 500,
    lineHeight    : '30px',
    width         : '100%',
    alignItems    : 'center',
    verticalAlign : 'middle'
  },
  options : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLink')
  }
}));

/**
 * @userProfileMobile
 */
export const UserProfileMobile = ({showProfile, handleClose}: UserProfileMobileProps) => {
  const classes       = useStyles();
  const dispatch      = useDispatch();
  const user          = useSelector(userSelector);
  const isLoggingOut  = useSelector(isAuthenticatingSelector);
  const accountName   = user?.account?.title;
  const username      = user.username;

  const onLogout = useCallback(function() {
    dispatch(signout());
  }, []);

  const onClose = useCallback(function() {
    handleClose?.();
  }, [handleClose]);

  function launchUserProfileDialog() {
    onClose();
    dispatch(getCurrentUser(user));
    dispatch(loadUsers());
    dispatch(showUserProfileDialog(true));
  };

  return (
    <Dialog fullScreen open={showProfile}>
      <div style={{width: '100%', height: '100%', overflow: 'hidden'}}>
        <div className={classes.container}>
          <div className={classes.userProfileHeader}>
            <IconButton icon={<MaterialExitCloseIcon />}
              style={{position: 'absolute'}}
              onClick={onClose}
              disableRipple />
            <HeaderTypography variant='h2' className={classes.profileTitle}>
              {translate('nav:header.account')}
            </HeaderTypography>
          </div>
        </div>
        <div style={{height: '70%'}}>
          <div className={classes.mainContainer}>
            <div className={classes.subContainer}>
              <div>
                <Avatar name={username} />
              </div>
              <div style={{height: '64px', marginLeft: '24px'}}>
                <label className={classes.user}>{username}</label>
                <label className={classes.account}>{accountName}</label>
              </div>
            </div>
            <div className={classes.profileItem}>
              <CTALink style={{marginLeft: 20}}
                className={classes.options}
                onClick={launchUserProfileDialog}>
                {translate('nav:header.profile')}
              </CTALink>
            </div>
          </div>
        </div>
        <div style={{height: '30%', marginLeft: '40px'}}>
          <CTALink style={{pointerEvents: isLoggingOut ? 'none' : 'initial', marginLeft: 20}}
            className={classes.options}
            onClick={onLogout}>
            {translate('nav:header.logout')}
          </CTALink>
        </div>
      </div>
    </Dialog>
  );
};
export default UserProfileMobile;
